import { useEffect, useRef } from 'react';

const ICLOSED_URL = 'https://app.iclosed.io/e/usinealead/rdv-45-min-votre-business-votre-besoin';

export default function HeroLP() {
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-6');
          }
        });
      },
      { threshold: 0.15 }
    );
    const els = statsRef.current?.querySelectorAll('[data-anim]');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="w-full flex flex-col"
      style={{ background: '#111111', fontFamily: '\'Inter\', sans-serif' }}
    >
      {/* Navbar */}
      <nav
        className="w-full flex items-center justify-between px-8 md:px-16 py-6"
        style={{ borderBottom: '1px solid rgba(200,146,42,0.15)' }}
      >
        <div className="flex items-center">
          <img
            src="https://storage.readdy-site.link/project_files/23ada950-d962-4505-8038-df56bbfe1d70/6ea77745-cd9f-4e56-bdbf-9c7d118c0c09_Logo-Usine-a-leads-_-fond-Sombre.png?v=2c85e2f6520a7370c78983fb86e22678"
            alt="Usine à Leads"
            className="h-12 w-auto"
            loading="eager"
            decoding="async"
          />
        </div>
        <button
          data-iclosed-link={ICLOSED_URL}
          data-embed-type="popup"
          className="text-sm font-semibold transition-all duration-200 hover:opacity-80 whitespace-nowrap cursor-pointer bg-transparent border-none p-0"
          style={{ color: '#C8922A' }}
        >
          Accéder au diagnostic →
        </button>
      </nav>

      {/* Hero body */}
      <div className="w-full px-8 md:px-16 pt-10 md:pt-14 pb-12 md:pb-16">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-7xl mx-auto">

          {/* Left — text */}
          <div className="flex flex-col gap-7">
            <p
              className="text-xs font-bold tracking-[0.25em] uppercase"
              style={{ color: 'rgba(255,255,255,0.45)' }}
            >
              Marc Signorel — Usine à Leads
            </p>

            <h1
              className="text-5xl md:text-6xl font-black leading-[1.05] text-white"
              style={{ fontFamily: '\'Inter\', sans-serif', letterSpacing: '-0.03em' }}
            >
              Votre site est en ligne.{' '}
              <br className="hidden md:block" />
              Mais il ne vous
              <br />
              <em
                className="not-italic"
                style={{
                  color: '#C8922A',
                  fontStyle: 'italic',
                  fontFamily: '\'Georgia\', serif',
                  fontWeight: 900,
                }}
              >
                rapporte rien.
              </em>
            </h1>

            <p
              className="text-base md:text-lg leading-relaxed max-w-md"
              style={{ color: 'rgba(255,255,255,0.6)' }}
            >
              Vous avez un site propre, une activité sérieuse, des clients satisfaits
              et pourtant votre agenda n&apos;est pas plein.{' '}
              <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                Ce n&apos;est pas un problème de crédibilité. C&apos;est un problème de système.
              </span>
            </p>

            <div className="flex flex-col gap-3 pt-2">
              <button
                data-iclosed-link={ICLOSED_URL}
                data-embed-type="popup"
                className="inline-flex items-center justify-center px-8 py-4 font-bold text-base transition-all duration-200 hover:brightness-110 active:scale-95 whitespace-nowrap cursor-pointer w-fit border-none"
                style={{
                  background: '#C8922A',
                  color: '#111111',
                  borderRadius: '4px',
                  letterSpacing: '-0.01em',
                }}
              >
                Accéder au diagnostic →
              </button>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                45 minutes offertes sans engagement
              </p>
            </div>
          </div>

          {/* Right — stats grid */}
          <div ref={statsRef} className="grid grid-cols-2 gap-3">
            {/* Stat 1 — gold bg */}
            <div
              data-anim
              className="flex flex-col gap-2 p-7 rounded-sm transition-all duration-700 opacity-0 translate-y-6"
              style={{ background: '#C8922A', transitionDelay: '0ms' }}
            >
              <span
                className="text-5xl md:text-6xl font-black leading-none"
                style={{ color: '#111111', letterSpacing: '-0.04em', fontFamily: '\'Inter\', sans-serif' }}
              >
                +47%
              </span>
              <span className="text-sm leading-snug font-medium" style={{ color: 'rgba(17,17,17,0.75)' }}>
                de leads qualifiés en moyenne dans les 90 premiers jours
              </span>
            </div>

            {/* Stat 2 — dark bg */}
            <div
              data-anim
              className="flex flex-col gap-2 p-7 rounded-sm transition-all duration-700 opacity-0 translate-y-6"
              style={{ background: '#1e1e1e', transitionDelay: '80ms' }}
            >
              <span
                className="text-5xl md:text-6xl font-black leading-none text-white"
                style={{ letterSpacing: '-0.04em', fontFamily: '\'Inter\', sans-serif' }}
              >
                100+
              </span>
              <span className="text-sm leading-snug" style={{ color: 'rgba(255,255,255,0.5)' }}>
                PME et indépendants accompagnés depuis 20 ans
              </span>
            </div>

            {/* Stat 3 — dark bg */}
            <div
              data-anim
              className="flex flex-col gap-2 p-7 rounded-sm transition-all duration-700 opacity-0 translate-y-6"
              style={{ background: '#1e1e1e', transitionDelay: '160ms' }}
            >
              <span
                className="text-5xl md:text-6xl font-black leading-none text-white"
                style={{ letterSpacing: '-0.04em', fontFamily: '\'Inter\', sans-serif' }}
              >
                20
              </span>
              <span className="text-sm leading-snug" style={{ color: 'rgba(255,255,255,0.5)' }}>
                ans d&apos;expérience terrain — pas de théorie
              </span>
            </div>

            {/* Stat 4 — dark bg */}
            <div
              data-anim
              className="flex flex-col gap-2 p-7 rounded-sm transition-all duration-700 opacity-0 translate-y-6"
              style={{ background: '#1e1e1e', transitionDelay: '240ms' }}
            >
              <span
                className="text-5xl md:text-6xl font-black leading-none text-white"
                style={{ letterSpacing: '-0.04em', fontFamily: '\'Inter\', sans-serif' }}
              >
                30j
              </span>
              <span className="text-sm leading-snug" style={{ color: 'rgba(255,255,255,0.5)' }}>
                pour avoir un système opérationnel et vos premiers RDV
              </span>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
